import React from 'react'
import PropTypes from 'prop-types'
import { capitalize, noop } from 'lodash'

import { useFormContext } from 'react-hook-form'
import { IconButton, useTheme, Grid } from '@mui/material'
import { XCircle } from '@styled-icons/heroicons-solid/XCircle'

import { roundToScale } from 'utils/price'
import { HHFormNumberField, HHFormPriceField, HHPriceField, HHFormSelectField } from 'components/form-fields/v5'
import { UNIT_OF_MEASURE, UNIT_OF_MEASURE_OPTIONS } from 'components/pricing/DisposalFeesV2/create-edit-disposal-fee-dialog/settings'

import T from 'T'
import DisposalMaterialPicker from 'components/work-order/details/disposal-ticket/DisposalTicketAccordion/DisposalMaterialPicker'

const MaterialRow = ({
  index,
  hasMultipleMaterials = false,
  disposalSiteId,
  disposalSiteMaterials = [],
  onDisposalSiteMaterialIdChange,
  onDelete,
}) => {
  const theme = useTheme()
  const { control, watch } = useFormContext()
  const unitOfMeasure = watch(`materials.${index}.unitOfMeasure`)
  const quantity = watch(`materials.${index}.quantity`)
  const unitPriceDollars = watch(`materials.${index}.unitPriceDollars`)
  const subtotal = roundToScale(quantity * unitPriceDollars)
  const netWeightLabel = [UNIT_OF_MEASURE.TON.value, UNIT_OF_MEASURE.POUND.value].includes(unitOfMeasure) ? T.NET_WEIGHT : T.QUANTITY

  return (
    <Grid container spacing={1} rowGap={2} justifyContent="space-between" alingItems="center" size={12}>
      <Grid container rowGap={2} size={{ xs: 12, lg: 5.5 }}>
        <Grid size={{ xs: 12, lg: 'grow' }}>
          <DisposalMaterialPicker
            rules={{ required: true }}
            onChange={onDisposalSiteMaterialIdChange}
            readOnly={!disposalSiteId}
            materials={disposalSiteMaterials}
            name={`materials.${index}.disposalSiteMaterialId`}
            control={control}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 'auto' }}>
          <HHFormSelectField
            sx={{ minWidth: 140 }}
            deprecatedLabel={false}
            label={capitalize(T.UNIT_OF_MEASURE)}
            name={`materials.${index}.unitOfMeasure`}
            control={control}
            options={UNIT_OF_MEASURE_OPTIONS}
            fullWidth
          />
        </Grid>
      </Grid>

      <Grid container rowGap={2} size={{ xs: 12, lg: 'grow' }}>
        <Grid size="grow">
          <HHFormNumberField
            deprecatedLabel={false}
            fullWidth
            decimalScale={8}
            control={control}
            name={`materials.${index}.quantity`}
            label={netWeightLabel}
          />
        </Grid>
        <Grid size="grow">
          <HHFormPriceField
            deprecatedLabel={false}
            fullWidth
            control={control}
            name={`materials.${index}.unitPriceDollars`}
            label={T.SITE_RATE}
          />
        </Grid>
        <Grid container alignItems="center" size={{ xs: 12, lg: 5 }}>
          <Grid size="grow">
            <HHPriceField
              disabled
              fixedDecimalScale
              deprecatedLabel={false}
              fullWidth
              value={subtotal}
              label={capitalize(T.TIPPING_FEE)}
              onChange={noop}
            />
          </Grid>
          {hasMultipleMaterials && (
            <Grid>
              <IconButton onClick={onDelete}>
                <XCircle width={20} height={20} color={theme.palette.text.secondary} />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

MaterialRow.propTypes = {
  index: PropTypes.number.isRequired,
  hasMultipleMaterials: PropTypes.bool,
  disposalSiteId: PropTypes.string.isRequired,
  disposalSiteMaterials: PropTypes.array.isRequired,
  onDisposalSiteMaterialIdChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default MaterialRow
